import classNames from 'clsx';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type Slider from 'react-slick';
import type { Settings } from 'react-slick';

import Arrow from '@swf/core/assets/icons/arrow.svg';

import Carousel from '../Carousel/Carousel';

import styles from './NavCarousel.scss';

export interface Props {
  slides: string[];
  settings?: Settings;
}

interface ArrowProps {
  className?: string;
  onClick?(): void;
}

const CrmNavArrow: React.FC<ArrowProps> = ({ onClick, className }) => (
  <Arrow onClick={onClick} className={className} />
);

const NavCarousel: React.FC<Props> = ({ settings, slides }) => {
  const mainSliderRef = useRef<Slider>(null);
  const navSliderRef = useRef<Slider>(null);
  const [mainSlider, setMainSlider] = useState<Slider>();
  const [navSlider, setNavSlider] = useState<Slider>();
  const [showArrows, setShowArrows] = useState(false);

  useEffect(() => {
    if (navSliderRef.current) {
      const { props } = navSliderRef.current;
      if (props?.children) {
        setShowArrows(
          !!props.children &&
            !!props.slidesToShow &&
            !!Array.isArray(props.children) &&
            props.children.length > props.slidesToShow
        );
      }
      setNavSlider(navSliderRef.current);
    }
    if (mainSliderRef.current) {
      setMainSlider(mainSliderRef.current);
    }
  }, []);

  const mainSliderSettings = useMemo(
    (): Settings => ({
      ...settings,
      fade: true,
      arrows: false,
      prevArrow: undefined,
      nextArrow: undefined,
      autoplay: true,
      autoplaySpeed: 7000,
      slidesToShow: 1,
      asNavFor: navSlider
    }),
    [navSlider, settings]
  );

  const navSliderSettings = useMemo(
    (): Settings => ({
      ...settings,
      arrows: true,
      infinite: true,
      slidesToShow: 4,
      slidesToScroll: 1,
      prevArrow: <CrmNavArrow />,
      nextArrow: <CrmNavArrow />,
      asNavFor: mainSlider
    }),
    [mainSlider, settings]
  );

  const navStyles = useMemo(
    () => classNames(styles.navSlider, showArrows ? styles.navArrows : ''),
    [showArrows]
  );

  const navigateToSlide = useCallback((event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    let slide = event.currentTarget;
    while (!slide.classList.contains('slick-slide') && slide.parentElement) {
      slide = slide.parentElement as HTMLDivElement;
    }
    const index = slide.dataset.index || '';
    const slideToGo: number = parseInt(index, 10);
    if (Number.isInteger(slideToGo) && mainSliderRef?.current) {
      mainSliderRef.current.slickGoTo(slideToGo, true);
    }
  }, []);

  return (
    <>
      <Carousel
        sliderRef={mainSliderRef}
        slides={slides}
        settings={mainSliderSettings}
        carouselClass={styles.mainSlider}
      />
      <Carousel
        sliderRef={navSliderRef}
        slides={slides}
        settings={navSliderSettings}
        onSlideClick={navigateToSlide}
        carouselClass={navStyles}
      />
    </>
  );
};

export default NavCarousel;
